/**
 * Invoice-related types and utilities
 */

export interface LineItem {
  description: string;
  quantity: number;
  price_cents: number;
}

export interface Invoice {
  id: number;
  tenant_id: number;
  invoice_number: string;
  contract_id?: number;
  customer_id: number;
  invoice_date: string;
  due_date: string;
  line_items: LineItem[];
  tax_rate: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  payment_status: 'unpaid' | 'partial' | 'paid' | 'overdue';
  paid_amount_cents: number;
  payment_method?: string;
  paid_at?: string;
  pdf_url?: string;
  is_cancelled: boolean;
  cancelled_at?: string;
  cancellation_invoice_id?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
  outstanding_amount_cents: number;
  is_overdue: boolean;
}

export interface InvoiceCreate {
  invoice_number: string;
  contract_id?: number;
  customer_id: number;
  invoice_date: string;
  due_date: string;
  line_items: LineItem[];
  tax_rate: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  payment_status: string;
  paid_amount_cents: number;
  payment_method?: string;
  paid_at?: string;
  pdf_url?: string;
  is_cancelled: boolean;
  cancelled_at?: string;
  cancellation_invoice_id?: number;
  notes?: string;
}

export interface InvoiceUpdate {
  invoice_number?: string;
  invoice_date?: string;
  due_date?: string;
  line_items?: LineItem[];
  tax_rate?: number;
  payment_status?: string;
  paid_amount_cents?: number;
  payment_method?: string;
  paid_at?: string;
  pdf_url?: string;
  notes?: string;
}

export interface PaymentUpdate {
  paid_amount_cents: number;
  payment_method: string;
  paid_at: string;
  notes?: string;
}

// Payment status labels for UI
export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  unpaid: 'Unbezahlt',
  partial: 'Teilweise bezahlt',
  paid: 'Bezahlt',
  overdue: 'Überfällig',
};

// Payment status colors for badges
export const PAYMENT_STATUS_COLORS: Record<string, string> = {
  unpaid: 'rounded-pill bg-muted text-muted-foreground',
  partial: 'rounded-pill bg-amber-500/15 text-amber-600 dark:text-amber-400',
  paid: 'rounded-pill bg-green-500/15 text-green-600 dark:text-green-400',
  overdue: 'rounded-pill bg-red-500/15 text-red-600 dark:text-red-400',
};

/**
 * Format price from cents to EUR string
 */
export function formatPrice(cents: number): string {
  return `${(cents / 100).toFixed(2)} €`;
}

/**
 * Parse price from EUR string to cents
 */
export function parsePrice(priceStr: string): number {
  const cleaned = priceStr.replace(/[^0-9,.]/g, '').replace(',', '.');
  const value = parseFloat(cleaned);
  return isNaN(value) ? 0 : Math.round(value * 100);
}

/**
 * Calculate line item total
 */
export function calculateLineItemTotal(item: LineItem): number {
  return Math.round(item.quantity * item.price_cents);
}

/**
 * Calculate invoice totals from line items
 */
export function calculateInvoiceTotals(
  lineItems: LineItem[],
  taxRate: number
): { subtotal: number; tax: number; total: number } {
  const subtotal = lineItems.reduce((sum, item) => sum + calculateLineItemTotal(item), 0);
  const tax = Math.round((subtotal * taxRate) / 100);
  const total = subtotal + tax;

  return { subtotal, tax, total };
}

/**
 * Format date for display (DD.MM.YYYY)
 */
export function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * Format date for input field (YYYY-MM-DD)
 */
export function formatInputDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toISOString().split('T')[0];
}

/**
 * Check if invoice is overdue
 */
export function isInvoiceOverdue(invoice: Invoice): boolean {
  if (invoice.payment_status === 'paid') return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(invoice.due_date);
  dueDate.setHours(0, 0, 0, 0);

  return dueDate < today && invoice.outstanding_amount_cents > 0;
}

/**
 * Calculate days until/past due date
 */
export function getDaysUntilDue(dueDateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDate = new Date(dueDateStr);
  dueDate.setHours(0, 0, 0, 0);

  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}
